import { SiteMeta } from '@src/types/SiteMeta';
import { TimeMode } from '@types';
import { getTsDueTimeMode } from '@utils/index_ts';
import dayjs, { Dayjs, ManipulateType } from 'dayjs';
import { TimePeriod, TimeRange } from './types';

export const getAdjustedTime = (
  dateFrame: Dayjs,
  siteMeta: SiteMeta,
  selectedTimeMode: TimeMode,
): string => {
  try {
    const isoDate: string = dayjs(dateFrame).toISOString();
    const timeModeAdjustmentIso = getTsDueTimeMode(
      siteMeta,
      selectedTimeMode,
      isoDate,
    );

    return timeModeAdjustmentIso.toISOString(true);
  } catch (_error) {
    console.error(
      'Time adjustment failed for date:',
      dateFrame.format(),
      'Error:',
      _error,
    );
    return dayjs(dateFrame).toISOString();
  }
};

export const parsePeriod = (
  period: TimePeriod,
): { value: number; unit: ManipulateType } | null => {
  if (period === 'custom') {
    return null;
  }

  const match = period.match(/^(\d+)(h|d)$/);
  if (!match) {
    return null;
  }

  const value = parseInt(match[1], 10);
  const unit = match[2] === 'h' ? 'hour' : 'day';

  return { value, unit };
};

export const calculateTimeRange = (
  period: TimePeriod,
  siteMeta: SiteMeta,
  timeMode: TimeMode,
  referenceTime?: Dayjs,
): TimeRange => {
  const now = referenceTime ?? dayjs();

  if (period === 'custom') {
    throw new Error('Cannot calculate time range for custom period');
  }

  const parsed = parsePeriod(period);
  if (!parsed) {
    throw new Error(`Invalid period format: ${period}`);
  }

  const from = now.subtract(parsed.value, parsed.unit);
  const to = now;

  const adjustedFrom = getAdjustedTime(from, siteMeta, timeMode);
  const adjustedTo = getAdjustedTime(to, siteMeta, timeMode);

  return {
    from: adjustedFrom,
    to: adjustedTo,
  };
};
